<template>
  <div>
    <RefreshResults
      v-if="showRefreshNotification"
      :type="'flight'"
      @clearResults="clearResults"
      @refreshSearch="getData(true)"
    />
    <div v-else>
      <div>
        <div class="pt-4">
          <HiddenResultsNotification
            v-if="hiddenItemsCount > 0"
            :hiddenItemsCount="hiddenItemsCount"
          />

          <template>
            <b-tabs
              v-if="tripVal !== TRIP_MULTI_CITY"
              v-model="activeTab"
              position="is-centered"
              type="is-toggle"
              id="flightSearchSwitcherTabs"
              :animated="false"
              size="is-small"
            >
              <b-tab-item
                label="Results by Price"
                class="fSwitcher"
                data-test="price_results"
              >
                <ResultsByPrice
                  :items="visibleItems"
                  :cacheKey="cacheKey"
                  :formData="formData"
                  @searchTargetedAirlines="searchTargetedAirlines"
                />
              </b-tab-item>
              <b-tab-item
                label="Results by Schedule"
                class="fSwitcher"
                data-test="schedule_results"
              >
                <ResultsBySchedule
                  :items="visibleItems"
                  :formData="formData"
                  @appendResults="appendResults"
                  @searchTargetedAirlines="searchTargetedAirlines"
                />
              </b-tab-item>
            </b-tabs>
            <ResultsByPrice
              v-else
              :items="visibleItems"
              :cacheKey="cacheKey"
              :formData="formData"
              @searchTargetedAirlines="searchTargetedAirlines"
              class="mt-5"
            />
          </template>
        </div>
      </div>

      <SearchErrorMessage
        v-if="errorState.hasError"
        :error_id="errorState.error_id"
        :errors="errorState.messages"
        :reportable="true"
      >
        <div v-if="items.length == 0">
          <p
            data-test="search-not-found"
            class="is-size-5 has-text-weight-bold"
          >
            <span>No results found.</span>
          </p>
          <p class="is-size-7 mb-2">
            Please change your search criteria and try again.
          </p>
        </div>

        <div v-else>
          <p
            data-test="search-not-found"
            class="is-size-6 has-text-weight-bold"
          >
            <span>Something went wrong.</span>
          </p>
          <p class="is-size-7 mb-2">
            Some search results failed to load. You can try again, or try
            another search.
          </p>
        </div>
      </SearchErrorMessage>

      <!-- Flight details modal -->
      <b-modal
        v-model="showDetails"
        has-modal-card
        trap-focus
        :destroy-on-hide="true"
        close-button-aria-label="Close"
        aria-modal
      >
        <div class="card p-3 modal-content-custom">
          <b-button
            label="Close"
            @click="closeDetails"
            type="is-info"
            expanded
            style="border-radius:0px"
            class="mb-4"
          />
          <FlightDetails v-if="showDetails" :journeys="flightDetailsJourneys" />
        </div>
      </b-modal>

      <!-- Fare option modal -->
      <b-modal
        v-model="showFareOptions"
        has-modal-card
        trap-focus
        :destroy-on-hide="true"
        close-button-aria-label="Close"
        aria-modal
      >
        <div class="card p-3">
          <div class="is-pulled-right">
            <div class="icon is-clickable" @click="closeFareOptions">
              <i class="mdi mdi mdi-24px mdi-close mdi mdi-18px"></i>
            </div>
          </div>

          <FareOptionsList
            v-if="showFareOptions"
            :solution="fareOptionsSolution"
          />
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import ResultsBySchedule from "./ResultsBySchedule"
import ResultsByPrice from "./ResultsByPrice"
import bookingMixin from "@/mixins/bookingMixin"
import HiddenResultsNotification from "../HiddenResultsNotification.vue"
import RefreshResults from "@/components/BookTravel/RefreshResults.vue"
import SearchErrorMessage from "@/components/BookTravel/SearchErrorMessage.vue"
import FlightDetails from "./FlightDetails.vue"
import FareOptionsList from "./FareOptionsList.vue"
import { mapState } from "vuex"

const ENDPOINTS = {
  CACHED_SEARCH: "amadeus/air/get-filtered-items",
  FRESH_SEARCH: "amadeus/air/search",
}

const DEFAULT_ERROR_MESSAGE = "Search failed or no results found."
const CACHE_LENGTH_SECONDS = 3600 // Move to configuration

export default {
  name: "FlightResults",
  mixins: [bookingMixin],
  props: {
    flightSearchToResultsBus: Object,
    formData: {
      type: Object,
    },
    cacheKey: {
      type: String,
      default: null,
    },
  },
  components: {
    ResultsBySchedule,
    HiddenResultsNotification,
    ResultsByPrice,
    RefreshResults,
    SearchErrorMessage,
    FlightDetails,
    FareOptionsList,
  },
  data() {
    return {
      activeTab: 0,
      showRefreshNotification: false,
      loaded: false,
      errors: [],
      error_id: null,
      skelItems: [{ id: 1 }, { id: 2 }, { id: 3 }],
      items: [],
      no_results: false,
      display_search: false,
      refreshTimer: null,
      uniqueItems: {},
      windowWidth: window.innerWidth,
      cacheLength: 1200, // how long to cache search results (in seconds) 600 seconds = 10 mins
      errorState: {
        hasError: false,
        messages: [],
        logId: null,
      },
    }
  },
  watch: {
    formData: {
      handler(newVal, prevVal) {
        this.showRefreshNotification = false
        this.loading = false
        this.getData()
      },
      deep: false,
    },

    // TODO test
    showRefreshNotification(newVal, prevVal) {
      this.setSearchRecordsStatus("flight", newVal)
    },
  },
  computed: {
    ...mapState(["travellersAndTrips"]),
    filterConfig() {
      return {
        filterOptions: this.filterOptions,
        preferredVendors: this.preferredVendors,
        sortOptions: this.sortOptions,
        unusedTickets: this.unusedTickets,
        tripVal: this.tripVal,
      }
    },
    filterOptions() {
      return this.$store.state.flight.filterOptions
    },
    filters: {
      get() {
        return this.$store.state.flight.filters
      },
      set(value) {
        this.$store.commit("flight/SET_FILTERS", value)
      },
    },
    unusedTickets() {
      return this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ].unusedTicketAirlines
    },
    loading: {
      get() {
        return this.$store.state.bookTravel.searchLoadingStatus
      },
      set(value) {
        this.$store.commit("bookTravel/FLIGHT_SEARCH_LOADING_STATUS", value)
      },
    },
    showFilterWarning: {
      get() {
        return this.$store.state.flight.showFilterWarning
      },
      set(value) {
        this.$store.commit("flight/SET_FILTER_WARNING", value)
      },
    },
    hiddenItemsCount() {
      let items = this.items

      if (items.length > 0) {
        items = items.filter((i) => {
          return !i.isVisible
        })
      }

      return items.length
    },
    //remove items that should be hidden based on travel policy restrictions from visible results
    visibleItems() {
      let items = this.items

      if (items.length > 0) {
        items = items.filter((i) => {
          return i.isVisible
        })
      }

      return items
    },
    sortOptions() {
      let options = []
      if (this.tripVal == "0") {
        options = [
          { value: "Rating", label: "Rating" },
          {
            value: "Time of day - Outbound",
            label: "Departure Time (outbound)",
          },
          { value: "Price Asc", label: "Price (Low to High)" },
          { value: "Price Desc", label: "Price (High to Low)" },
          { value: "Duration", label: "Duration" },
        ]
      } else if (this.tripVal == "1") {
        options = [
          { value: "Rating", label: "Rating" },
          {
            value: "Time of day - Outbound",
            label: "Departure Time (outbound)",
          },
          { value: "Time of day - Return", label: "Departure Time (return)" },
          { value: "Price Asc", label: "Price (Low to High)" },
          { value: "Price Desc", label: "Price (High to Low)" },
          { value: "Duration", label: "Duration" },
        ]
      } else {
        options = [
          { value: "Rating", label: "Rating" },
          { value: "Price Asc", label: "Price (Low to High)" },
          { value: "Price Desc", label: "Price (High to Low)" },
          { value: "Duration", label: "Duration" },
        ]
      }

      if (this.preferredVendors.length > 0) {
        options.push({
          value: "Preferred Vendors",
          label: "Preferred Airlines",
        })
      }

      return options
    },
    loc1() {
      return this.formData.loc1.code
    },
    loc2() {
      return this.formData.loc2.code
    },
    startDate() {
      return this.formData.startDate.date
    },
    endDate() {
      return this.formData.endDate.date
    },
    startTime() {
      return this.formData.startDate.time
    },
    endTime() {
      return this.formData.endDate.time
    },
    tripVal() {
      return this.formData.tripVal
    },
    loadingMessage() {
      return this.loading
        ? "Searching flights"
        : "Looking for additional flight results"
    },
    showErrors() {
      return (
        this.loading == false &&
        (this.errors.length > 0 || this.items.length == 0)
      )
    },
    showDetails: {
      get() {
        return this.$store.state.bookTravel.flightDetails?.show || false
      },
      set(val) {
        let details = {
          show: val,
        }

        this.$store.commit("bookTravel/SET_FLIGHT_DETAILS", details)
      },
    },
    flightDetailsJourneys() {
      return this.$store.state.bookTravel.flightDetails.journeys
    },
    fareOptionsSolution() {
      return this.$store.state.bookTravel.fareOptions.solution
    },
    showFareOptions: {
      get() {
        return this.$store.state.bookTravel.fareOptions?.show || false
      },
      set(val) {
        let details = {
          show: val,
        }

        this.$store.commit("bookTravel/SET_FARE_OPTIONS", details)
      },
    },
    profile() {
      return this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ]
    },
    preferredVendors() {
      return this.profile.preferredAirVendors || []
    },
  },
  methods: {
    buildSearchBody({
      preferredAirlines = [],
      mandatoryAirlines = [],
      excludedAirlines = [],
      numberOfUpsells = null,
      cacheKey = null,
    }) {
      let body = {
        numberOfPassengers: 1,
        numberOfResults: 400,
        itinerary: [],
        travelerId: this.$globals.travellerProfile.id,
        applyPolicy: false,
        cacheKey: cacheKey, // if included, will retrieve cached results
        cacheResponse: true, // whether to cache the response
        cacheExpiration: this.cacheLength, // how long to cache the response (in seconds)
        unusedTicketAirlineCodes: this.profile.unusedTicketAirlines || [],
      }

      if (this.tripVal === this.TRIP_MULTI_CITY) {
        let itinerary = []
        for (let row of this.formData.multiCity) {
          let leg = {
            date: {
              date: moment(row.depDate.date).format("YYYY-MM-DD"),
            },
          }

          if (row.depDate.advanced.tod !== "Anytime") {
            leg.date = this.setAdvancedDateTime(row.depDate)
            leg.date.timeWindow = 4
          }

          if (row.fromLoc.subType === "AIRPORT") {
            leg.departureLocation = {
              airport: row.fromLoc.code,
            }
          }
          if (row.fromLoc.subType === "CITY") {
            leg.departureLocation = {
              city: row.fromLoc.cityCode,
            }
          }
          if (row.toLoc.subType === "AIRPORT") {
            leg.arrivalLocation = {
              airport: row.toLoc.code,
            }
          }
          if (row.toLoc.subType === "CITY") {
            leg.arrivalLocation = {
              city: row.toLoc.cityCode,
            }
          }
          itinerary.push(leg)
        }
        body.itinerary = itinerary
      } else {
        let locType1 = "airport"
        let locType2 = "airport"

        if (this.formData.loc1.subType == "CITY") {
          locType1 = "city"
        }
        if (this.formData.loc2.subType == "CITY") {
          locType2 = "city"
        }

        if (this.tripVal === this.TRIP_ONE_WAY) {
          body.itinerary = [
            {
              departureLocation: {
                [locType1]: this.loc1,
              },
              arrivalLocation: {
                [locType2]: this.loc2,
              },
              date: {
                date: moment(this.startDate).format("YYYY-MM-DD"),
              },
            },
          ]
        } else if (this.tripVal === this.TRIP_ROUNDTRIP) {
          body.itinerary = [
            {
              departureLocation: {
                [locType1]: this.loc1,
              },
              arrivalLocation: {
                [locType2]: this.loc2,
              },
              date: {
                date: moment(this.startDate).format("YYYY-MM-DD"),
              },
            },
            {
              departureLocation: {
                [locType2]: this.loc2,
              },
              arrivalLocation: {
                [locType1]: this.loc1,
              },
              date: {
                date: moment(this.endDate).format("YYYY-MM-DD"),
              },
            },
          ]
        }

        // Set the number of upsells to include
        if (numberOfUpsells !== null) {
          body.numberOfUpsells = numberOfUpsells
        }

        if (this.formData.startDate.advanced.tod !== "Anytime") {
          body.itinerary[0].date = this.setAdvancedDateTime(
            this.formData.startDate
          )
        }

        if (this.formData.endDate.advanced.tod !== "Anytime") {
          body.itinerary[1].date = this.setAdvancedDateTime(
            this.formData.endDate
          )
        }
      }

      if (
        this.$store.getters["travellersAndTrips/getTravellers"].tripType ===
        "business"
      ) {
        body.travelPolicyId = this.$globals.travellerProfile.travelPolicyId
        body.applyPolicy = true
      }

      if (this.formData.cabinClass) {
        body.cabinClass = this.formData.cabinClass
      }

      if (this.isMeeting) {
        if (this.meetingInfo.meetingId !== null) {
          body.meetingId = this.meetingInfo.meetingId
        }
      }

      // Add preferred / mandatory airlines
      if (mandatoryAirlines.length > 0 && !this.formData.ndcOnly) {
        body.mandatoryAirlines = mandatoryAirlines
      }

      if (preferredAirlines.length > 0) {
        body.preferredAirlines = preferredAirlines
      }

      // Add excluded airlines
      if (excludedAirlines.length > 0 && !this.formData.ndcOnly) {
        body.excludedAirlines = excludedAirlines
      }

      //Include NDC results if this comes from the Browser Extension
      if (this.formData.ndcOnly) {
        body.ndcOnly = true
        body.cacheResponse = false
      }

      let atcDetails = this.$store.state.bookTravel.atc
      if (atcDetails?.useAtcShopper) {
        body.useAtcShopper = true
        body.ticketNumbers = Array(atcDetails.ticketNumber)
        body.mandatoryAirlines = atcDetails.ticketRules.airlines
        body.applyPolicy = true

        // Max 200 results for ATC Shopper
        body.numberOfResults = 200

        const atcJourneys = atcDetails.journeys.map((journey) => {
          return {
            requestCode: journey.requestCode,
            stops: journey.stops,
          }
        })

        body.atcJourneys = atcJourneys
        body.excludeNdc = true //disable NDC results for ATC Shopper
        body.preferredAirlines = null //disable preferred airlines for ATC Shopper
      }

      return body
    },

    async getData(freshSearch = false) {
      if (!this.cacheKey) {
        freshSearch = true
      }

      this.resetState(freshSearch)

      try {
        const { endpoint, body } = this.getSearchConfig(freshSearch)

        const result = await this.$app.huddleLabsServerCall(endpoint, body)

        if (result.data?.length) {
          await this.handleSuccess(result)
        } else {
          if (result.meta?.cacheExpired) {
            this.showRefreshNotification = true
          } else if (!freshSearch) {
            this.showFilterWarning = true
          } else {
            throw new Error(result.errors?.[0].title || DEFAULT_ERROR_MESSAGE)
          }
        }
      } catch (error) {
        this.errorState = {
          hasError: true,
          messages: Array({ title: error.message }),
        }
        this.handleError(error.message)
      } finally {
        this.loading = false

        if (freshSearch) {
          this.finalizeItemsLoading("flight")
        }
      }
    },

    resetState(fresh = false) {
      this.loading = true
      this.errorState = { hasError: false, messages: [], logId: null }
      this.showRefreshNotification = false
      this.showFilterWarning = false

      if (fresh) {
        this.$store.commit("flight/RESET_FILTERS")

        // Clear selected unbundled journeys, if selected
        if (this.$store.state.bookTravel.selectedUnbundledJourneys.length > 0) {
          this.$store.commit("bookTravel/CLEAR_SELECTED_UNBUNDLED_JOURNEYS")
        }
      }
    },

    getSearchConfig(fresh = false) {
      const preferredAirlines = this.$store.getters[
        "travellersAndTrips/getCurrentTravellerProfile"
      ].preferredAirVendors

      let config = {
        endpoint: ENDPOINTS.FRESH_SEARCH,
        body: this.buildSearchBody({
          preferredAirlines,
          numberOfUpsells: 3,
        }),
      }

      if (this.cacheKey && !fresh) {
        config = {
          endpoint: ENDPOINTS.CACHED_SEARCH,
          body: {
            cacheKey: this.cacheKey,
          },
        }
        if (this.filters) {
          config.body.filters = this.filters
        }
      }

      return config
    },

    async handleSuccess(result) {
      const { data, meta, included } = result

      if (included.filterOptions) {
        this.updateFilterOptions(included.filterOptions)
      }

      this.items = data.map((item) =>
        this.transformFlightData(
          item,
          included.fareFamilyInfo,
          meta.benchmarkPrice
        )
      )

      if (this.updateCache(meta)) {
        await this.updateSearchCache(meta.cacheKey)
      }

      //console.log("count before: ", this.items.length)
      //this.items = this.groupUpsells(this.items)
      //console.log("count after: ", this.items.length)
    },

    transformFlightData(item, fareFamilyInfo, benchmarkPrice) {
      // Skip if multi-city trip
      if (this.tripVal === this.TRIP_MULTI_CITY) {
        return item
      }

      const transformedItem = { ...item }

      /*
      // Add pricing to journeys
      transformedItem.journeys[0].pricing = item.pricing
      if (this.tripVal === this.TRIP_ROUNDTRIP) {
        transformedItem.journeys[1].pricing = item.pricing
      }

      // Add fare family info
      transformedItem.fareFamily = fareFamilyInfo.find(
        (fareFamily) => fareFamily.id === item.fareFamilyId
      )
      */

      transformedItem.benchmarkPrice = benchmarkPrice

      // Handle ATC Shopper policy
      if (this.$store.state.bookTravel.atc?.useAtcShopper) {
        transformedItem.isVisible = !item.travelPolicy?.summary
          ?.hardApprovalRequiredCount
      }

      return transformedItem
    },

    updateCache(meta) {
      return meta.cacheKey && !meta.isCachedResult
    },

    async updateSearchCache(cacheKey) {
      // Save search form data and key to Vuex
      this.$store.commit("bookTravel/SAVE_SEARCH", {
        expiresAt: moment()
          .add(CACHE_LENGTH_SECONDS, "seconds")
          .format("YYYY-MM-DD HH:mm:ss"),
        key: cacheKey,
        formData: JSON.parse(JSON.stringify(this.formData)),
      })

      // Update URL if needed
      if (this.$route.query.key !== cacheKey) {
        await this.$router.push({
          path: "flights",
          query: { key: cacheKey },
        })
      }
    },

    updateFilterOptions(filterOptions) {
      this.$store.commit("flight/SET_FILTER_OPTIONS", filterOptions)

      const lowestFare = Math.min(...filterOptions.priceData)
      const highestFare = Math.max(...filterOptions.priceData)

      if (this.filters.maxPrice == 20000) {
        this.filters.maxPrice = highestFare
      }
      if (this.filters.minPrice == 0) {
        this.filters.minPrice = lowestFare
      }
    },

    handleError(error) {},
    groupUpsells(items) {
      // Group solutions by uniqueKey using reduce
      const groupedSolutions = items.reduce((acc, solution) => {
        if (!acc[solution.uniqueKey]) {
          acc[solution.uniqueKey] = []
        }
        acc[solution.uniqueKey].push(solution)
        return acc
      }, {})

      // Create maps for base solutions and upsell solutions
      const baseSolutions = new Map()

      // First, identify base solutions
      Object.entries(groupedSolutions).forEach(([uniqueKey, solutions]) => {
        // Take the first solution as base solution and store its ID
        baseSolutions.set(uniqueKey, solutions[0].id)
      })

      // Process all solutions to set up upsells
      const processedSolutions = items.filter((solution) => {
        const uniqueKey = solution.uniqueKey
        const baseSolutionId = baseSolutions.get(uniqueKey)

        if (solution.id === baseSolutionId) {
          // If this is a base solution, add upsells to it
          const allSolutionsInGroup = groupedSolutions[uniqueKey]
          // Remove the base solution from the group to get only upsells
          const upsells = allSolutionsInGroup.slice(1)

          // Add upsell information to the base solution
          solution.upsellsCount = upsells.length
          solution.upsells = upsells

          return true // Keep this solution in the filtered array
        }

        return false // Remove non-base solutions from the filtered array
      })

      // Update the solutions array with only base solutions and their upsells
      return processedSolutions
    },

    //search for specific airline(s)
    async searchTargetedAirlines(targetedAirlines = []) {
      if (targetedAirlines.length < 1) {
        return
      }

      let body = this.buildSearchBody({
        mandatoryAirlines: targetedAirlines,
        numberOfUpsells: 3,
      })

      this.loading = true
      let result = await this.$app.huddleLabsServerCall(
        "amadeus/air/search",
        body
      )

      this.loading = false

      if (result.data && result.data.length > 0) {
        this.appendResults({
          results: result.data,
          included: result.included,
        })

        // Update URL if needed
        const cacheKey = result.meta.cacheKey
        if (cacheKey && this.$route.query.key !== cacheKey) {
          this.$router.push({
            path: "flights",
            query: { key: cacheKey },
          })
        }
      } else if (result.errors) {
        //show error message returned from server
        let errors = result.errors //Object.entries(result.errors);
        this.errors = this.errors.concat(errors)
        this.error_id = result.error_log_id
      } else {
        //show default error message
        let errors = { title: "No results found for targeted airlines." }
        this.errors.push(errors)
      }

      this.$store.commit("bookTravel/FLIGHT_SEARCH_LOADING_STATUS", false)
    },

    //append new flight results to this.items array
    appendResults({ results, included }) {
      if (results.length) {
        //this.addSolutionKeyToJourneys(results);

        let fareFamilyInfo = included.fareFamilyInfo

        for (let i = 0; i < results.length; i++) {
          let result = results[i]

          //make sure we are not adding any duplicate results
          let found = this.items.find(
            (item) => item.uniqueKey === result.uniqueKey
          )

          if (!found) {
            // FOR SEARCH BY SCHEDULE PRICE RANGES
            if (result.journeys.length == 1) {
              //add pricing to outbound (one-way)
              result.journeys[0].pricing = result.pricing
            } else {
              //add pricing to return (roundtrip)
              result.journeys[1].pricing = result.pricing
            }

            // add the fare family info to the result
            let matchingFareFamily = fareFamilyInfo.find(
              (fareFamily) => fareFamily.id === result.fareFamilyId
            )

            result.fareFamily = matchingFareFamily

            // add the fare family info to the upsells
            if (result.upsellsCount > 0) {
              for (let k = 0; k < result.upsells.length; k++) {
                let upsell = result.upsells[k]
                let matchingFareFamily = fareFamilyInfo.find(
                  (fareFamily) => fareFamily.id === upsell.fareFamilyId
                )

                upsell.fareFamily = matchingFareFamily
              }
            }

            if (i == 1) {
              console.log(result)
            }

            //append item to items
            this.items.push(result)
          }
        }
      }

      //add to additional flight results counter
      //this.$store.commit("bookTravel/ADD_MORE_FLIGHT_RESULTS_MULTIPLE", results);
    },
    setAdvancedDateTime(obj) {
      //console.dir(obj);

      let date = {
        date: moment(obj.date).format("YYYY-MM-DD"),
        time: null,
        timeWindow: 4,
        isDeparture: obj.advanced.isDeparture, //.toString(),
      }

      if (obj.advanced.tod == "Morning") {
        date.time = "8:00"
      }

      if (obj.advanced.tod == "Afternoon") {
        date.time = "13:00"
      }

      if (obj.advanced.tod == "Evening") {
        date.time = "18:00"
      }

      if (obj.advanced.tod == "Custom") {
        date.time = moment(obj.advanced.time).format("HH:mm")
        date.timeWindow = obj.advanced.hours
      }

      return date
    },

    dateParse(date) {
      return (
        date.getFullYear() +
        "-" +
        (date.getMonth() > 8
          ? date.getMonth() + 1
          : "0" + (date.getMonth() + 1)) +
        "-" +
        (date.getDate() > 9 ? date.getDate() : "0" + date.getDate())
      )
    },
    closeDetails() {
      this.$store.commit("bookTravel/HIDE_FLIGHT_DETAILS")
    },
    closeFareOptions() {
      this.$store.commit("bookTravel/HIDE_FARE_OPTIONS")
    },
  },
  async mounted() {
    this.$store.commit("bookTravel/HIDE_FLIGHT_DETAILS")
    this.$store.commit("bookTravel/HIDE_FARE_OPTIONS")

    this.setSearchRecordsStatus("flight", this.showRefreshNotification)
    window.onresize = () => {
      this.windowWidth = window.innerWidth
    }
    //
    this.clearBundledSelections()

    await this.getData()
  },
  created() {
    // Set some constants so we can reference them in a meaningful way
    // in the search request builder and other places.
    this.TRIP_ONE_WAY = 0
    this.TRIP_ROUNDTRIP = 1
    this.TRIP_MULTI_CITY = 2
    this.ARRIVAL_TIME = 1
    this.DEPARTURE_TIME = 0
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer)
  },
  provide() {
    return {
      //handle the filter change event
      onFilterChange: (value) => {
        //console.log("filter event fired", value)
        this.getData()
      },
    }
  },
}
</script>

<style scoped>
.modal-content-custom {
  max-height: 800px;
  overflow-y: auto;
}

.loading:after {
  overflow: hidden;
  display: inline-block;
  vertical-align: bottom;
  -webkit-animation: ellipsis steps(4, end) 900ms infinite;
  animation: ellipsis steps(4, end) 900ms infinite;
  content: "\2026"; /* ascii code for the ellipsis character */
  width: 0px;
}

@keyframes ellipsis {
  to {
    width: 1.25em;
  }
}

@-webkit-keyframes ellipsis {
  to {
    width: 1.25em;
  }
}

@media screen and (max-width: 768px) {
  .modal.is-active {
    justify-content: flex-start;
    overflow-y: auto;
  }
}
</style>
